import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { formatCurrency, formatDate } from '../utils/dateUtils.ts';
import {
  getAssets,
  getOperations,
  createOperation,
  updateOperation,
  deleteOperation,
  FIIAsset,
  FIIOperation,
  CreateFIIOperationRequest,
} from '../services/fiiApi.ts';
import './FIIOperations.css';

function getCurrentCompetence(): string {
  const now = new Date();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const y = now.getFullYear();
  return `${m}/${y}`;
}

function competenceToDateRange(competence: string): { start_date: string; end_date: string } {
  const [month, year] = competence.split('/');
  const m = parseInt(month, 10);
  const y = parseInt(year, 10);
  const start = `${y}-${String(m).padStart(2, '0')}-01`;
  const lastDay = new Date(y, m, 0).getDate();
  const end = `${y}-${String(m).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
  return { start_date: start, end_date: end };
}

function buildCompetenceOptions(): string[] {
  const opts: string[] = [];
  const now = new Date();
  for (let i = 0; i < 24; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const y = d.getFullYear();
    opts.push(`${m}/${y}`);
  }
  return opts;
}

const FIIOperations: React.FC = () => {
  const [operations, setOperations] = useState<FIIOperation[]>([]);
  const [assets, setAssets] = useState<FIIAsset[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingOp, setEditingOp] = useState<FIIOperation | null>(null);

  const [filterCompetence, setFilterCompetence] = useState(getCurrentCompetence);
  const [filterTicker, setFilterTicker] = useState<number | ''>('');
  const [filterType, setFilterType] = useState<string>('');

  const [form, setForm] = useState<CreateFIIOperationRequest>({
    fii_asset_id: 0,
    date: '',
    type: 'buy',
    quantity: 1,
    unit_price: 0,
  });
  const [irrfManuallySet, setIrrfManuallySet] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  // Default rate retido na fonte ("dedo-duro") on FII sales: 0,005%.
  const DEDO_DURO_RATE = 0.00005;
  const computeDefaultIRRF = (qty: number, price: number) =>
    Math.round(qty * price * DEDO_DURO_RATE * 100) / 100;
  const suggestedIRRF = useMemo(
    () => computeDefaultIRRF(form.quantity, form.unit_price),
    [form.quantity, form.unit_price]
  );

  const competenceOptions = useMemo(() => buildCompetenceOptions(), []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const ass = await getAssets();
      setAssets(ass);
      const { start_date, end_date } = competenceToDateRange(filterCompetence);
      const params: { start_date: string; end_date: string; fii_asset_id?: number; type?: string } = {
        start_date,
        end_date,
      };
      if (filterTicker) params.fii_asset_id = filterTicker as number;
      if (filterType) params.type = filterType;
      const ops = await getOperations(params);
      setOperations(ops as FIIOperation[]);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Erro ao carregar operações');
    } finally {
      setLoading(false);
    }
  }, [filterCompetence, filterTicker, filterType]);

  useEffect(() => { load(); }, [load]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      // For sells, send the IRRF (manual or auto-suggested). For buys, omit so backend stores 0.
      const irrf = form.type === 'sell'
        ? (irrfManuallySet ? form.irrf : suggestedIRRF)
        : undefined;
      if (editingOp) {
        await updateOperation(editingOp.id, {
          date: form.date,
          type: form.type as 'buy' | 'sell',
          quantity: form.quantity,
          unit_price: form.unit_price,
          irrf,
        });
      } else {
        await createOperation({ ...form, irrf });
      }
      setShowForm(false);
      setEditingOp(null);
      resetForm();
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Erro ao salvar operação');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (op: FIIOperation) => {
    if (!window.confirm('Confirma exclusão desta operação?')) return;
    setDeletingId(op.id);
    try {
      await deleteOperation(op.id);
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Erro ao excluir');
    } finally {
      setDeletingId(null);
    }
  };

  const startEdit = (op: FIIOperation) => {
    setEditingOp(op);
    setForm({
      fii_asset_id: op.fii_asset_id,
      date: op.date.substring(0, 10),
      type: op.type,
      quantity: op.quantity,
      unit_price: op.unit_price,
      irrf: op.irrf,
    });
    // Treat persisted IRRF as manual when it diverges from the default suggestion.
    const defaultIrrf = computeDefaultIRRF(op.quantity, op.unit_price);
    setIrrfManuallySet(op.type === 'sell' && Math.abs((op.irrf ?? 0) - defaultIrrf) > 0.005);
    setShowForm(true);
  };

  const resetForm = () => {
    setForm({ fii_asset_id: 0, date: '', type: 'buy', quantity: 1, unit_price: 0 });
    setIrrfManuallySet(false);
    setEditingOp(null);
  };

  const tickerFor = (assetId: number) =>
    assets.find(a => a.id === assetId)?.ticker ?? `#${assetId}`;

  return (
    <div className="fii-operations">
      <div className="page-header">
        <div>
          <h1>🔄 Operações FII</h1>
          <p>Registre compras e vendas de Fundos Imobiliários</p>
        </div>
        <button className="primary-button" onClick={() => { resetForm(); setShowForm(true); }}>
          + Nova Operação
        </button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="filters-bar">
        <div className="filter-group">
          <label>Competência</label>
          <select
            value={filterCompetence}
            onChange={e => setFilterCompetence(e.target.value)}
          >
            {competenceOptions.map(c => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>
        <div className="filter-group">
          <label>Ticker</label>
          <select
            value={filterTicker}
            onChange={e => setFilterTicker(e.target.value ? parseInt(e.target.value, 10) : '')}
          >
            <option value="">Todos</option>
            {assets.map(a => (
              <option key={a.id} value={a.id}>{a.ticker}</option>
            ))}
          </select>
        </div>
        <div className="filter-group">
          <label>Tipo</label>
          <select
            value={filterType}
            onChange={e => setFilterType(e.target.value)}
          >
            <option value="">Todos</option>
            <option value="buy">Compra</option>
            <option value="sell">Venda</option>
          </select>
        </div>
      </div>

      {showForm && (
        <div className="form-container">
          <h3>{editingOp ? 'Editar Operação' : 'Nova Operação'}</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label>FII</label>
                <select
                  value={form.fii_asset_id}
                  onChange={e => setForm(f => ({ ...f, fii_asset_id: parseInt(e.target.value) }))}
                  required
                  disabled={!!editingOp}
                >
                  <option value={0}>Selecione...</option>
                  {assets.map(a => (
                    <option key={a.id} value={a.id}>{a.ticker}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Tipo</label>
                <select
                  value={form.type}
                  onChange={e => setForm(f => ({ ...f, type: e.target.value as 'buy' | 'sell' }))}
                >
                  <option value="buy">Compra</option>
                  <option value="sell">Venda</option>
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Data</label>
                <input
                  type="date"
                  value={form.date}
                  onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
                  required
                />
              </div>
              <div className="form-group">
                <label>Quantidade</label>
                <input
                  type="number"
                  min={1}
                  value={form.quantity}
                  onChange={e => setForm(f => ({ ...f, quantity: parseInt(e.target.value) || 1 }))}
                  required
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Preço Unitário (R$)</label>
                <input
                  type="number"
                  min={0.01}
                  step={0.01}
                  value={form.unit_price}
                  onChange={e => setForm(f => ({ ...f, unit_price: parseFloat(e.target.value) || 0 }))}
                  required
                />
              </div>
              <div className="form-group">
                <label>Total Estimado</label>
                <input
                  type="text"
                  value={formatCurrency(form.quantity * form.unit_price)}
                  disabled
                />
              </div>
            </div>
            {form.type === 'sell' && (
              <div className="form-row">
                <div className="form-group">
                  <label title="0,005% sobre o valor da venda — retido pela corretora; pode ser deduzido da DARF apurada">
                    IRRF Retido (dedo-duro)
                  </label>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input
                      type="number"
                      min={0}
                      step={0.01}
                      value={irrfManuallySet ? (form.irrf ?? 0) : suggestedIRRF}
                      onChange={e => {
                        setIrrfManuallySet(true);
                        setForm(f => ({ ...f, irrf: parseFloat(e.target.value) || 0 }));
                      }}
                    />
                    {irrfManuallySet && (
                      <button
                        type="button"
                        className="secondary-button"
                        onClick={() => {
                          setIrrfManuallySet(false);
                          setForm(f => ({ ...f, irrf: undefined }));
                        }}
                        title="Voltar ao cálculo automático (0,005%)"
                      >
                        ↻ Auto
                      </button>
                    )}
                  </div>
                  <small style={{ color: '#6b7280' }}>
                    Sugerido: {formatCurrency(suggestedIRRF)} (0,005% × valor da venda).
                    Confira o valor exato na nota de corretagem.
                  </small>
                </div>
              </div>
            )}
            <div className="form-actions">
              <button type="submit" className="primary-button" disabled={saving}>
                {saving ? 'Salvando...' : (editingOp ? 'Salvar' : 'Registrar')}
              </button>
              <button
                type="button"
                className="secondary-button"
                onClick={() => { setShowForm(false); resetForm(); }}
                disabled={saving}
              >
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="loading">Carregando operações...</div>
      ) : operations.length === 0 ? (
        <div className="empty-state">
          <span className="placeholder-icon">🔄</span>
          <p>Nenhuma operação registrada ainda.</p>
        </div>
      ) : (
        <div className="table-container">
          <table className="ops-table">
            <thead>
              <tr>
                <th>Data</th>
                <th>Ticker</th>
                <th>Tipo</th>
                <th>Qtd</th>
                <th>P. Unitário</th>
                <th>Total</th>
                <th title="IRRF retido na fonte (dedo-duro) — só em vendas">IRRF</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {operations.map(op => (
                <tr key={op.id}>
                  <td>{formatDate(op.date.substring(0, 10))}</td>
                  <td><span className="ticker-badge">{tickerFor(op.fii_asset_id)}</span></td>
                  <td>
                    <span className={`type-badge ${op.type === 'buy' ? 'buy' : 'sell'}`}>
                      {op.type === 'buy' ? 'Compra' : 'Venda'}
                    </span>
                  </td>
                  <td className="number-cell">{op.quantity}</td>
                  <td className="number-cell">{formatCurrency(op.unit_price)}</td>
                  <td className="number-cell">{formatCurrency(op.total_value)}</td>
                  <td className="number-cell">
                    {op.type === 'sell' ? formatCurrency(op.irrf ?? 0) : '—'}
                  </td>
                  <td className="actions-cell">
                    <div className="actions-wrapper">
                      <button className="action-icon-button" onClick={() => startEdit(op)} title="Editar">✏️</button>
                      <button className="action-icon-button danger" onClick={() => handleDelete(op)} title="Excluir" disabled={deletingId === op.id}>{deletingId === op.id ? '...' : '🗑️'}</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default FIIOperations;
